import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import {
  Save,
  AlertCircle,
  Loader2,
  RefreshCw,
} from "lucide-react";

import { useCompanySettings, UPLOAD_KEYS, CompanySettingsProvider } from "../../../contexts/CompanySettingsContext";
import ErrorBoundary from "../../../components/ErrorBoundary";
import ConfirmModalProvider from "../../../components/admin/ConfirmModal";

// Import shared components
import LoadingStatus from "../../../components/admin/shared/LoadingStatus";

// Import form section components
import CompanyInfoForm from "../../../components/admin/company/CompanyInfoForm";
import ConventionDefaultsForm from "../../../components/admin/company/ConventionDefaultsForm";
import TermsForm from "../../../components/admin/company/TermsForm";

/**
 * Type definitions (these would be in .d.ts files in a TypeScript project)
 * 
 * @typedef {Object} CompanySettings
 * @property {string} companyName - Name of the company
 * @property {string} contactEmail - Primary contact email
 * @property {string} contactPhone - Contact phone number
 * @property {string} disclaimerText - Legal text/disclaimers
 * @property {string} conventionName - Default name for new conventions
 * @property {string} conventionDescription - Default description for new conventions
 * @property {string} logoUrl - URL to company logo image
 * @property {string} conventionLogoUrl - URL to default convention logo image
 * @property {string} organizerId - ID of the organization that owns these settings
 * 
 * @typedef {Object} UploadResponse
 * @property {string} uploadUrl - Presigned S3 URL for upload
 * @property {string} publicUrl - Public URL where the file will be accessible
 * 
 * @typedef {Object} AuthContext
 * @property {string} organizerId - ID of the logged-in user's organization
 * @property {boolean} isAuthenticated - Whether the user is authenticated
 */

/**
 * Main Content Component for Company Settings
 * Contains form state and handlers
 */
const CompanySettingsContent = () => {
  // Component state for file handling
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [defaultConventionLogoFile, setDefaultConventionLogoFile] = useState(null);
  const [defaultConventionLogoPreview, setDefaultConventionLogoPreview] = useState("");
  
  // Get context values
  const {
    state,
    initialize,
    uploadFile,
    saveSettings,
    showNotification,
    setHasUnsavedChanges,
  } = useCompanySettings();
  
  const {
    existingSettings,
    isInitializing,
    isSaving,
    isUploading,
    uploadProgress,
    notification,
    hasUnsavedChanges
  } = state;
  
  // Calculate if loading
  const isLoading = isInitializing || isUploading || isSaving;

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      companyName: "",
      contactEmail: "",
      contactPhone: "",
      disclaimerText: "Enter disclaimers or legal text here...",
      conventionName: "",
      conventionDescription: "",
    },
    mode: "onBlur",
  });

  // Initialize component
  useEffect(() => {
    (async () => {
      try {
        await initialize();
      } catch (err) {
        console.error('Failed to initialize company settings:', err);
      }
    })();
  }, [initialize]);

  // Set form values when settings are loaded
  useEffect(() => {
    if (existingSettings) {
      setValue("companyName", existingSettings.companyName || "");
      setValue("contactEmail", existingSettings.contactEmail || "");
      setValue("contactPhone", existingSettings.contactPhone || "");
      setValue(
        "disclaimerText",
        existingSettings.disclaimerText ||
          "Enter disclaimers or legal text here..."
      );
      setValue("conventionName", existingSettings.conventionName || "");
      setValue(
        "conventionDescription",
        existingSettings.conventionDescription || ""
      );

      // Set logo previews if they exist
      if (existingSettings.logoUrl) {
        setLogoPreview(existingSettings.logoUrl);
      }
      if (existingSettings.conventionLogoUrl) {
        setDefaultConventionLogoPreview(existingSettings.conventionLogoUrl);
      }
    }
  }, [existingSettings, setValue]);

  // Update unsaved changes state
  useEffect(() => {
    const calculatedHasUnsavedChanges = isDirty || logoFile !== null || defaultConventionLogoFile !== null;
    setHasUnsavedChanges(calculatedHasUnsavedChanges);
  }, [isDirty, logoFile, defaultConventionLogoFile, setHasUnsavedChanges]);

  // Handle beforeunload event when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle company logo file selection
  const handleLogoChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showNotification("error", "Logo file must be under 5MB");
      return;
    }

    setLogoFile(file);
  }, [showNotification]);

  // Process logo file with proper cleanup when file changes
  useEffect(() => {
    if (!logoFile) return;
    
    const reader = new FileReader();
    
    reader.onload = (event) => {
      setLogoPreview(event.target.result);
    };
    
    reader.onerror = () => {
      showNotification("error", "Failed to read image file");
    };
    
    reader.readAsDataURL(logoFile);
    
    // Proper cleanup
    return () => {
      reader.abort();
    };
  }, [logoFile, showNotification]);

  // Handle convention logo file selection
  const handleConventionLogoChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showNotification("error", "Convention logo file must be under 5MB");
      return;
    }

    setDefaultConventionLogoFile(file);
  }, [showNotification]);

  // Process convention logo file with proper cleanup when file changes
  useEffect(() => {
    if (!defaultConventionLogoFile) return;
    
    const reader = new FileReader();
    
    reader.onload = (event) => {
      setDefaultConventionLogoPreview(event.target.result);
    };
    
    reader.onerror = () => {
      showNotification("error", "Failed to read image file");
    };
    
    reader.readAsDataURL(defaultConventionLogoFile);
    
    // Proper cleanup
    return () => {
      reader.abort();
    };
  }, [defaultConventionLogoFile, showNotification]);

  // Form submission handler
  const onSubmit = useCallback(
    async (data) => {
    try {
      console.log("Saving company settings...");
      let logoUrl = existingSettings?.logoUrl || null;
      let conventionLogoUrl = existingSettings?.conventionLogoUrl || null;

      // Parallel logo uploads for efficiency
      const uploadPromises = [];

      // Upload company logo if a new file was selected
      if (logoFile) {
        uploadPromises.push(
            uploadFile(logoFile, UPLOAD_KEYS.COMPANY_LOGO)
            .then((url) => {
              logoUrl = url;
            })
            .catch((error) => {
              console.error("Company logo upload failed:", error);
              throw new Error(`Company logo upload failed: ${error.message}`);
            })
        );
      }

      // Upload convention logo if a new file was selected
      if (defaultConventionLogoFile) {
        uploadPromises.push(
            uploadFile(defaultConventionLogoFile, UPLOAD_KEYS.CONVENTION_LOGO)
            .then((url) => {
              conventionLogoUrl = url;
            })
            .catch((error) => {
              console.error("Convention logo upload failed:", error);
              throw new Error(
                `Convention logo upload failed: ${error.message}`
              );
            })
        );
      }

      // Wait for all uploads to complete
      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }

      // Prepare data with file URLs
      const settingsData = {
        ...data,
        logoUrl,
        conventionLogoUrl,
      };

      // Save settings
      const success = await saveSettings(settingsData);

      if (success) {
        // Reset form state to mark as clean with the current values
        reset(data, { keepValues: true });

        // Reset file states to prevent re-upload but keep previews
        setLogoFile(null);
        setDefaultConventionLogoFile(null);
          
        // Update previews with the new URLs
        if (logoUrl && logoUrl !== logoPreview) {
          setLogoPreview(logoUrl);
        }
        if (conventionLogoUrl && conventionLogoUrl !== defaultConventionLogoPreview) {
          setDefaultConventionLogoPreview(conventionLogoUrl);
        }
      }
    } catch (error) {
        console.error("Error in form submission:", error);
        showNotification("error", `Error: ${error.message}`);
      }
    },
    [
      existingSettings,
      logoFile,
      logoPreview,
      defaultConventionLogoFile,
      defaultConventionLogoPreview,
      uploadFile,
      saveSettings,
      reset,
      showNotification,
    ]
  );

  // Form reset handler
  const handleReset = useCallback(() => {
    reset();
    setLogoFile(null);
    setDefaultConventionLogoFile(null);

    // Reset to existing settings if available
    if (existingSettings) {
      setValue("companyName", existingSettings.companyName || "");
      setValue("contactEmail", existingSettings.contactEmail || "");
      setValue("contactPhone", existingSettings.contactPhone || "");
      setValue(
        "disclaimerText",
        existingSettings.disclaimerText ||
          "Enter disclaimers or legal text here..."
      );
      setValue("conventionName", existingSettings.conventionName || "");
      setValue(
        "conventionDescription",
        existingSettings.conventionDescription || ""
      );

      // Reset previews to remote URLs
      setLogoPreview(existingSettings.logoUrl || "");
      setDefaultConventionLogoPreview(existingSettings.conventionLogoUrl || "");
    } else {
      // If no settings exist, clear previews
      setLogoPreview("");
      setDefaultConventionLogoPreview("");
    }
  }, [existingSettings, reset, setValue]);

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="rounded-box p-6 bg-base-100 border border-base-300">
        <div className="flex flex-col lg:flex-row justify-between gap-6">
          <div>
            <h1 className="text-4xl font-bold text-base-content">
              Company Settings
            </h1>
            <p className="text-base-content/60 mt-2">
              Manage your company information, branding, and convention defaults
            </p>
          </div>
          <div className="flex items-center">
            <button
              onClick={handleReset}
              className="btn btn-outline mr-3"
              disabled={isLoading || !hasUnsavedChanges}
              aria-busy={isLoading}
              type="button"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
              )}
              Reset
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isLoading || !hasUnsavedChanges}
              onClick={handleSubmit(onSubmit)}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2
                    className="w-5 h-5 animate-spin mr-2"
                    aria-hidden="true"
                  />
                  <LoadingStatus
                    isInitializing={isInitializing}
                    isUploading={isUploading}
                    isSaving={isSaving}
                  />
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" aria-hidden="true" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div role="alert" className={`alert ${notification.type === 'error' ? 'alert-error' : 'alert-success'} flex items-center`}>
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>{notification.message}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Company Information */}
        <CompanyInfoForm
          register={register}
          errors={errors}
          logoPreview={logoPreview}
          handleLogoChange={handleLogoChange}
          uploadProgress={uploadProgress}
          isUploading={isUploading}
        />

        {/* Convention Defaults */}
        <ConventionDefaultsForm
          register={register}
          conventionLogoPreview={defaultConventionLogoPreview}
          handleConventionLogoChange={handleConventionLogoChange}
          uploadProgress={uploadProgress}
          isUploading={isUploading}
        />

        {/* Terms */}
        <TermsForm register={register} />
      </form>
    </div>
  );
};

/**
 * Main Company Settings Component
 * Wraps the content with necessary providers
 */
const CompanySettings = () => {
  return (
    <CompanySettingsProvider>
      <ConfirmModalProvider>
        <ErrorBoundary>
          <CompanySettingsContent />
        </ErrorBoundary>
      </ConfirmModalProvider>
    </CompanySettingsProvider>
  );
};

export default CompanySettings;
